import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { WorkCarousel } from './components/WorkCarousel';
import { CaseStudy } from './components/CaseStudy';
import { Process } from './components/Process';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';
import { Reveal } from './components/ui/Reveal';
import { Marquee } from './components/ui/Marquee';
import { LoadingScreen } from './components/ui/LoadingScreen'; // Import Loading Screen
import { cn, generateVideoThumbnail } from './lib/utils';
import { AnimatePresence } from 'framer-motion';
import { CDN_URL } from './constants';

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simula il tempo di caricamento o l'animazione del brand
    // 2.5 secondi è un buon compromesso tra branding e UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2800);

    return () => clearTimeout(timer);
  }, []);

  /**
   * LOGHI PARTNER OTTIMIZZATI (SVG/JPG)
   * Usiamo optimizeCloudinary con quality 'eco' per i loghi del Marquee.
   * Sono piccoli e in movimento, la qualità ultra-HD non serve, la velocità sì.
   */
  const partners = [
    { src: `${CDN_URL}/LOGHI/prosciutto%20parma.jpg`, alt: "Prosciutto Parma" },
    { src: `${CDN_URL}/LOGHI/mritz%20logo.jpg`, alt: "Manuel Ritz" },
    { src: `${CDN_URL}/LOGHI/Mela_logo_transparentBlue-minimal.png`, alt: "Mela Works" },
    { src: `${CDN_URL}/LOGHI/Mapei_logo.svg.png`, alt: "Mapei" },
    { src: `${CDN_URL}/LOGHI/logo-tombolini.jpg`, alt: "Tombolini" },
    { src: `${CDN_URL}/LOGHI/LOGO-REPO-RED.png`, alt: "Repo Red" },
    { src: `${CDN_URL}/LOGHI/logo_andrea_sanseverinati.png`, alt: "Andrea Severinati" },
    { src: `${CDN_URL}/LOGHI/Logo%20shada.png`, alt: "Shada" },
    { src: `${CDN_URL}/LOGHI/linfa_menu_big.png`, alt: "Linfa" },
    { src: `${CDN_URL}/LOGHI/Risorsa%201smashero.png`, alt: "Smashero" },
    { src: `${CDN_URL}/LOGHI/la%20rotonda.png`, alt: "LaRotonda" },
    { src: `${CDN_URL}/LOGHI/images%20(4).jpg`, alt: "SaraPokeTiAmo" },
    { src: `${CDN_URL}/LOGHI/Helios-Logo-Color.png`, alt: "Helios" },
    { src: `${CDN_URL}/LOGHI/galassia-store-logo.png`, alt: "GalassiaStore" },
    { src: `${CDN_URL}/LOGHI/FIERACAVALLI_ITA.png`, alt: "Fieracavalli" },
    { src: `${CDN_URL}/LOGHI/fantasanremo-project.jpg-10.png`, alt: "Fantasanremo" },
    { src: `${CDN_URL}/LOGHI/ed_logo_verde.png`, alt: "Ernestodoland" },
    { src: `${CDN_URL}/LOGHI/e1ed7a302aa8dd68c24c565e9a6e9b18.png`, alt: "Heineken" },
    { src: `${CDN_URL}/LOGHI/bontempi%20logo.jpg`, alt: "Bontempi" },
  ];

  const caseStudiesData = [
    {
      client: "Giulio il Sarto",
      description: "Come abbiamo trasformato un sarto artigiano in un brand da 15.000 follower e clienti in lista d’attesa Giulio, un giovane sarto con laboratorio artigianale, intenzionato a farsi largo in un settore dominato da estetiche classiche e comunicazione impostata",
      // Generiamo l'anteprima dal video invece che usare Unsplash
      image: generateVideoThumbnail(`${CDN_URL}/CASI%20STUDIO/giulio%20caso%20studio.mp4`, 600, 'good'),
      videoUrl: `${CDN_URL}/CASI%20STUDIO/giulio%20caso%20studio.mp4`,
      stats: [
        { label: "Followers", value: "+16k", sub: "Org." },
        { label: "Views", value: "+3,7 MLN", sub: "Tot." }
      ]
    },
    {
      client: "Zat",
      description: "da tatuatore locale a creator virale: come abbiamo fatto crescere Zat da 5.000 a 15.000 follower in 4 mesi Zat, tatuatore con uno stile distintivo e una clientela fidelizzata, ma ancora poco visibile online. L’obiettivo? Far emergere la sua personalità, attrarre nuovi clienti e costruire una community solida.",
      image: generateVideoThumbnail(`${CDN_URL}/CASI%20STUDIO/ric%20caso%20studio%20zat.mp4`, 600, 'good'),
      videoUrl: `${CDN_URL}/CASI%20STUDIO/ric%20caso%20studio%20zat.mp4`,
      stats: [
        { label: "Followers", value: "+16k", sub: "Org." },
        { label: "Views", value: "+3,4 MLN", sub: "Tot." }
      ],
      reverse: true
    },
    {
      client: "Moon Digital Agency",
      description: "Caso Studio: Da 0 a 2 Milioni di Visualizzazioni in 30 Giorni – La Strategia Dietro la Crescita di Moon Digital Agency Negli ultimi 30 giorni ho trasformato il mio profilo social in una vera macchina da contenuti. Il risultato? Oltre 2 milioni di visualizzazioni - Più di 8000 nuovi follower - 4 nuovi clienti acquisiti in organico Non si è trattato di fortuna, ma di una strategia precisa, testata e replicabile",
      image: generateVideoThumbnail(`${CDN_URL}/CASI%20STUDIO/FOCUS%20caso%20studio%20ric.mp4`, 600, 'good'),
      videoUrl: `${CDN_URL}/CASI%20STUDIO/FOCUS%20caso%20studio%20ric.mp4`,
      stats: [
        { label: "Followers", value: "+10k", sub: "Org." },
        { label: "Views", value: "+2,5 MLN", sub: "Tot." }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white selection:bg-white selection:text-black font-sans">
      <AnimatePresence>
        {isLoading && <LoadingScreen key="loading" />}
      </AnimatePresence>

      <Navbar />
      
      <div id="page-content" className="relative w-full">
        <main>
          <Hero />
          
          <div className="relative z-20 -mt-24 md:-mt-32 bg-black">
            <Marquee items={partners} />
          </div>

          <section id="work" className="bg-black relative">
              <WorkCarousel />
              
              <div className="relative pt-12 pb-12 overflow-hidden bg-black border-t border-white/10">
                
                <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
                    <div
                        className={cn(
                          "absolute inset-0",
                          "[background-size:20px_20px]",
                          "[background-image:radial-gradient(#404040_1px,transparent_1px)]"
                        )}
                    />
                    <div className="absolute inset-0 bg-black [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 md:px-6 mb-8 md:mb-12 text-center relative z-10">
                  <Reveal width="100%">
                    <h2 className="text-4xl md:text-7xl font-bold font-['Space_Grotesk'] tracking-tighter uppercase">
                      CASE <span className="text-zinc-700">STUDIES</span>
                    </h2>
                  </Reveal>
                </div>

                <div className="max-w-6xl mx-auto px-0 md:px-6 relative z-10">
                   {/* 
                      LAYOUT:
                      Compact Vertical Stack.
                      Gap reduced to gap-8 (mobile) / gap-12 (desktop).
                   */}
                   <div className="flex flex-col gap-8 md:gap-12">
                      {caseStudiesData.map((study, idx) => (
                        <CaseStudy 
                          key={idx}
                          {...study}
                        />
                      ))}
                   </div>
                </div>
              </div>
          </section>

          <Process />
          <Contact />
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default App;